import { DASHBOARD_ROUTES } from '../../constants/dashboardRoutes';
import { canAccessTradeWorkspace } from '../../utils/brokerConnection';
import { videoCaptureUiMode } from '../../utils/videoCaptureLayout';
import { PRO_GOLD, PRO_GOLD_RGB } from '../billing/billingPlanData';

export { PRO_GOLD } from '../billing/billingPlanData';

/** Coming soon pill — distinct from PRO_GOLD and muted nav text */
const COMING_SOON_BADGE_COLOR = '#6EB5E8';
const COMING_SOON_BADGE_BG = 'rgba(66, 153, 225, 0.14)';
const COMING_SOON_BADGE_BORDER = '1px solid rgba(66, 153, 225, 0.42)';

const PRO_BADGE_BG = `rgba(${PRO_GOLD_RGB}, 0.15)`;
const PRO_BADGE_BORDER = `1px solid rgba(${PRO_GOLD_RGB}, 0.45)`;

const NAV_BADGE_BASE = {
  flexShrink: 0,
  padding: '2px 6px',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderRadius: 999,
  lineHeight: 1.2,
};

/** Shared nav pill styles — DashboardSidebar + MobileMenuDropdown */
export function getNavBadgeStyle(variant = 'rose') {
  if (variant === 'pro') {
    return {
      ...NAV_BADGE_BASE,
      color: PRO_GOLD,
      backgroundColor: PRO_BADGE_BG,
      border: PRO_BADGE_BORDER,
    };
  }
  if (variant === 'soon') {
    return {
      ...NAV_BADGE_BASE,
      color: COMING_SOON_BADGE_COLOR,
      backgroundColor: COMING_SOON_BADGE_BG,
      border: COMING_SOON_BADGE_BORDER,
    };
  }
  return {
    ...NAV_BADGE_BASE,
    color: '#ff4f70',
    backgroundColor: 'rgba(255, 79, 112, 0.12)',
    border: '1px solid rgba(255, 79, 112, 0.35)',
  };
}

/** Superuser / special nav accents (mobile + header parity) */
export const NAV_ACCENT_GOLD = PRO_GOLD;
export const NAV_ACCENT_RED = '#ef4444';

export function getTradeRoute(_user) {
  return '/trade';
}

/** Landing path when Trade workspace is unavailable. */
export function getPortfolioRoute() {
  return DASHBOARD_ROUTES.overview;
}

/** Match useIsMobile(768, 1024) for non-React callers (e.g. HomeRedirect). */
export function isMobileViewport() {
  try {
    const capture = videoCaptureUiMode();
    if (capture === 'mobile') return true;
    if (capture === 'desktop') return false;
    const hasTouch = typeof window !== 'undefined' && 'ontouchstart' in window;
    const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
    return width <= 768 || (hasTouch && width <= 1024);
  } catch (_) {
    return false;
  }
}

/** Allow /trade on mobile when explicitly opening the trade workspace (not a cold load). */
export function canOpenMobileTradeRoute(location) {
  if (!location) return false;
  try {
    const params = new URLSearchParams(location.search || '');
    if (params.get('symbol')) return true;
    if (videoCaptureUiMode(location.search || '') === 'mobile') return true;
  } catch (_) {}
  const state = location.state;
  return state?.page === 'trade' || state?.openTrade === true;
}

/** Post-auth home: Trade workspace when any linked connection has market data. Mobile → Portfolio. */
export function getAppHomeRoute(user) {
  if (isMobileViewport()) {
    return getPortfolioRoute();
  }
  if (canAccessTradeWorkspace(user) === false) {
    return getPortfolioRoute();
  }
  return getTradeRoute(user);
}

/** Main pages + tools + configuration — shared by DashboardSidebar and mobile menu */
export function buildDashboardPrimarySections(
  tradeRoute,
  { showTrade = true, showProBadges = false, showMarketDataPages = true, showPositionsOverview, showBacktest = false, showRsStudy = false } = {},
) {
  const pageItems = [];
  if (showTrade) {
    pageItems.push({
      type: 'link',
      to: tradeRoute,
      label: 'Trade',
      exact: false,
      icon: 'graphUp',
    });
  }
  pageItems.push(
    { type: 'link', to: DASHBOARD_ROUTES.overview, label: 'Portfolio', exact: true, icon: 'overview' },
    {
      type: 'link',
      to: DASHBOARD_ROUTES.performance,
      label: 'Performance',
      exact: true,
      icon: 'performance',
      ...(showProBadges ? { badge: 'Trade', badgeVariant: 'pro' } : {}),
    },
    { type: 'link', to: DASHBOARD_ROUTES.health, label: 'Health', exact: true, icon: 'health' },
  );

  const includePositionsOverview = showPositionsOverview ?? showMarketDataPages;
  const marketDataItems = showMarketDataPages
    ? [
        { type: 'link', to: DASHBOARD_ROUTES.marketOverview, label: 'Market Overview', exact: true, icon: 'globe' },
        { type: 'link', to: DASHBOARD_ROUTES.marketRelativeStrength, label: 'Relative Strength', exact: true, icon: 'activity' },
        ...(includePositionsOverview
          ? [{ type: 'link', to: DASHBOARD_ROUTES.marketPositions, label: 'Positions Overview', exact: true, icon: 'barChart' }]
          : []),
      ]
    : [];

  const backtestItems = showBacktest
    ? [{ type: 'link', to: DASHBOARD_ROUTES.backtest, label: 'Backtest', exact: true, icon: 'clockHistory' }]
    : [];

  const rsStudyItems = showRsStudy
    ? [{ type: 'link', to: DASHBOARD_ROUTES.rsStudy, label: 'RS Study', exact: true, icon: 'graphUp' }]
    : [];

  return [
    {
      title: 'Pages',
      defaultOpen: true,
      isFirst: true,
      items: pageItems,
    },
    {
      title: 'Tools',
      defaultOpen: true,
      items: [
        { type: 'link', to: DASHBOARD_ROUTES.ai, label: 'PrecisionAI', icon: 'robot' },
        {
          type: 'link',
          to: DASHBOARD_ROUTES.aiAutomations,
          label: 'AI Automations',
          icon: 'clockHistory',
          exact: false,
          addTo: `${DASHBOARD_ROUTES.aiAutomations}/new`,
          addLabel: 'Add AI alert',
        },
        { type: 'link', to: DASHBOARD_ROUTES.research, label: 'Research', icon: 'beaker' },
        ...marketDataItems,
        ...backtestItems,
        ...rsStudyItems,
      ],
    },
    {
      title: 'Workspace',
      defaultOpen: true,
      items: [
        { type: 'link', to: DASHBOARD_ROUTES.tradingRules, label: 'Trading Rules', icon: 'checklist' },
        { type: 'link', to: DASHBOARD_ROUTES.journals, label: 'Journals', icon: 'journalPage' },
        { type: 'link', to: DASHBOARD_ROUTES.watchlists, label: 'Watchlists', icon: 'list' },
        { type: 'link', to: DASHBOARD_ROUTES.alerts, label: 'Alerts', icon: 'bell' },
      ],
    },
  ];
}

export const DASHBOARD_LEARNING_SECTION = {
  title: 'Learning',
  defaultOpen: false,
  items: [
    { type: 'link', to: '/learn#quick-start', label: 'Quick Start' },
    { type: 'link', to: '/learn#trading-fundamentals', label: 'Trading Fundamentals' },
    { type: 'link', to: '/learn#software-guides', label: 'Software Guides' },
  ],
};

/** Bottom account links — Settings, Contact, Content Creator, Admin (sidebar source of truth) */
export function buildDashboardAccountSection({ showUpgradeToPro, isSuperuser }) {
  const items = [];
  if (showUpgradeToPro) {
    items.push({ type: 'upgrade' });
  }
  items.push({ type: 'link', to: DASHBOARD_ROUTES.settings, label: 'Settings', icon: 'gear' });
  items.push({ type: 'link', to: '/contact', label: 'Contact', icon: 'envelope' });
  if (isSuperuser) {
    items.push({
      type: 'link',
      to: '/content-creator',
      label: 'Content Creator',
      accent: 'gold',
      icon: 'images',
    });
    items.push({ type: 'link', to: '/admin', label: 'Admin', accent: 'red', icon: 'danger' });
  }
  return {
    title: 'Account',
    defaultOpen: true,
    items,
  };
}

/** Mobile-only items appended after sidebar Account links */
export function buildMobileAccountExtras({ hasPaidSubscription, isOnTradeShell }) {
  const items = [];
  if (!hasPaidSubscription) {
    items.push({ type: 'link', to: '/pricing', label: 'Pricing', icon: 'creditCard' });
  }
  if (isOnTradeShell) {
    items.push({ type: 'reportBug', label: 'Report Bug', icon: 'bug' });
  }
  items.push({ type: 'logout', label: 'Logout', icon: 'logout' });
  return items;
}
